import axios from "axios";
import React, {
  useEffect,
  useState,
  useMemo
} from "react";
import "./Dashboard.css";
import PageTitle from "../components/PageTitle";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const BASE_URL =
  process.env.REACT_APP_API_URL;

const STATUS_LABELS = {
  submitted: "Registrado",
  pdf_ready: "PDF listo",
  sent_to_sign: "En firma",
  signed: "Firmado",
  rejected: "Rechazado",
  expired: "Vencido"
};

function Dashboard() {

  const [dashboardData, setDashboardData] =
    useState({
      trendData: [],
      topDepartamentos: [],
      topEmpresas: [],
      lastFive: [],
      totals: {
        total: 0,
        signed: 0,
        sent_to_sign: 0,
        pending: 0
      }
    });

  const [loading, setLoading] =
    useState(true);

  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  useEffect(() => {

    const loadDashboard =
      async () => {

        try {

          setLoading(true);

          const res =
            await axios.get(
              `${BASE_URL}/api/admin/affiliations/dashboard/affiliations`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          setDashboardData(
            res.data.data || {
              trendData: [],
              topDepartamentos: [],
              topEmpresas: [],
              lastFive: [],
              totals: {
                total: 0,
                signed: 0,
                sent_to_sign: 0,
                pending: 0
              }
            }
          );

        } catch (err) {

          if (
            err.response?.status === 401 ||
            err.response?.status === 403
          ) {
            return;
          }

          console.error(
            "❌ Error cargando dashboard:",
            err
          );

        } finally {

          setLoading(false);

        }

      };

    loadDashboard();

  }, [
    token
  ]);

  const hoy =
    useMemo(
      () => new Date(),
      []
    );

  const nombreMes =
    hoy.toLocaleDateString(
      "es-CO",
      {
        month: "long"
      }
    );

  const nombreMesCapitalizado =
    nombreMes.charAt(0).toUpperCase() +
    nombreMes.slice(1);

  const trendData =
    useMemo(() => {

      return (
        dashboardData.trendData || []
      ).map((item) => {

        const fecha =
          new Date(
            `${item.fecha}T00:00:00`
          );

        return {
          fecha:
            fecha.toLocaleDateString(
              "es-CO",
              {
                day: "2-digit",
                month: "short"
              }
            ),
          afiliaciones:
            item.afiliaciones || 0
        };

      });

    }, [
      dashboardData.trendData
    ]);

  const topDepartamentos =
    dashboardData.topDepartamentos || [];

  const topEmpresas =
    dashboardData.topEmpresas || [];

  const lastFive =
    dashboardData.lastFive || [];

  if (loading) {

    return (

      <div className="dash-root">

        <div className="dash-loading">
          Cargando analítica...
        </div>

      </div>

    );

  }

  return (

    <>

    <PageTitle title="Dashboard" />

    <div className="dash-root">

      <div className="dash-card">

        <div className="dash-card-header">

          <h3>
            Tendencia de actividad
          </h3>

          <span className="dash-card-subtitle">
            Afiliaciones recibidas últimos 30 días
          </span>

        </div>

        <div className="dash-chart-wrapper">

          <ResponsiveContainer
            width="100%"
            height={260}
          >

            <AreaChart
              data={trendData}
            >

              <defs>

                <linearGradient
                  id="colorAff"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >

                  <stop
                    offset="10%"
                    stopColor="#07111a"
                    stopOpacity={0.8}
                  />

                  <stop
                    offset="90%"
                    stopColor="#0d1a25"
                    stopOpacity={0}
                  />

                </linearGradient>

              </defs>

              <CartesianGrid
                stroke="#eee"
                strokeDasharray="4 4"
              />

              <XAxis
                dataKey="fecha"
                tick={{
                  fontSize: 14
                }}
                minTickGap={50}
              />

              <YAxis
                allowDecimals={false}
              />

              <Tooltip />

              <Area
                type="monotone"
                dataKey="afiliaciones"
                stroke="#07111a"
                fillOpacity={1}
                fill="url(#colorAff)"
                name="Afiliaciones"
              />

            </AreaChart>

          </ResponsiveContainer>

        </div>

      </div>

      <div className="dash-grid-2">

        <div className="dash-card">

          <div className="dash-card-header">

            <h3>
              Top por departamentos
            </h3>

            <span className="dash-card-subtitle">

              Departamentos con más afiliaciones recibidas durante{" "}

              <b>
                {nombreMesCapitalizado}
              </b>

            </span>

          </div>

          {topDepartamentos.length === 0 ? (

            <p className="dash-empty">
              Aún no hay afiliaciones este mes.
            </p>

          ) : (

            <div className="dash-chart-wrapper small">

              <ResponsiveContainer
                width="100%"
                height={220}
              >

                <BarChart
                  data={topDepartamentos.slice(
                    0,
                    6
                  )}
                >

                  <defs>

                    <linearGradient
                      id="barDepartamentos"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >

                      <stop
                        offset="0%"
                        stopColor="#07111a"
                        stopOpacity={0.9}
                      />

                      <stop
                        offset="100%"
                        stopColor="#0d1a25"
                        stopOpacity={0.2}
                      />

                    </linearGradient>

                  </defs>

                  <CartesianGrid
                    stroke="#eee"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="departamento"
                  />

                  <YAxis
                    allowDecimals={false}
                  />

                  <Tooltip />

                  <Bar
                    dataKey="cantidad"
                    fill="url(#barDepartamentos)"
                    radius={[
                      20,
                      20,
                      0,
                      0
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          )}

        </div>

        <div className="dash-card">

          <div className="dash-card-header">

            <h3>
              Top por empresas
            </h3>

            <span className="dash-card-subtitle">

              Empresas con más afiliaciones recibidas durante{" "}

              <b>
                {nombreMesCapitalizado}
              </b>

            </span>

          </div>

          {topEmpresas.length === 0 ? (

            <p className="dash-empty">
              Aún no hay afiliaciones este mes.
            </p>

          ) : (

            <table className="dash-table">

              <thead>

                <tr>

                  <th>
                    Empresa
                  </th>

                  <th
                    style={{
                      textAlign: "right"
                    }}
                  >
                    Afiliaciones
                  </th>

                </tr>

              </thead>

              <tbody>

                {topEmpresas.map((e) => (

                  <tr key={e.empresa}>

                    <td>
                      {e.empresa}
                    </td>

                    <td
                      style={{
                        textAlign: "right"
                      }}
                    >

                      {Number(
                        e.cantidad || 0
                      ).toLocaleString(
                        "es-CO"
                      )}

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          )}

        </div>

      </div>

      <div className="dash-card">

        <div className="dash-card-header">

          <h3>
            Últimas afiliaciones
          </h3>

          <span className="dash-card-subtitle">
            Últimas 5 afiliaciones registradas
          </span>

        </div>

        {lastFive.length === 0 ? (

          <p className="dash-empty">
            Aún no hay afiliaciones registradas.
          </p>

        ) : (

          <table className="dash-table">

            <thead>

              <tr>

                <th>
                  Radicado
                </th>

                <th>
                  Documento
                </th>

                <th>
                  Nombre
                </th>

                <th>
                  Estado
                </th>

                <th>
                  Fecha
                </th>

              </tr>

            </thead>

            <tbody>

              {lastFive.map((a) => (

                <tr key={a._id}>

                  <td>
                    {a.radicado}
                  </td>

                  <td>
                    {a.numeroDocumento}
                  </td>

                  <td>
                    {a.nombres}
                  </td>

                  <td>
                    {STATUS_LABELS[a.status] || a.status}
                  </td>

                  <td>
                    {new Date(
                      a.createdAt
                    ).toLocaleString(
                      "es-CO"
                    )}
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        )}

      </div>

    </div>

   </>

  );

}

export default Dashboard;